import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { Subject } from 'rxjs';

/** Minimum pixels the drag must travel before we acknowledge it as an intentional pull. */
const DEAD_ZONE = 8;

/**
 * Max distance (px) the indicator descends from its hidden position above the
 * top edge. Overlay pattern: only the indicator moves — the content/header stay
 * put — so this is small (just enough to reveal the indicator), not a
 * full-content overscroll.
 */
const INDICATOR_TRAVEL = 72;

/** Progress-ring geometry (matches the 36x36 viewBox in the template). */
const RING_RADIUS = 14;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Returns the nearest scrollable ancestor of el, or null if none found before root. */
function findScrollableAncestor(
    el: HTMLElement | null,
    root: HTMLElement
): HTMLElement | null {
    let node = el;
    while (node && node !== root) {
        const overflowY = getComputedStyle(node).overflowY;
        if (
            (overflowY === 'auto' || overflowY === 'scroll') &&
            node.scrollHeight > node.clientHeight
        ) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}

/**
 * Custom pull-to-refresh wrapper. Drop-in replacement for NgxPullToRefreshComponent.
 *
 * Inputs:
 *  - `isEnable`           – gate controlled by LayoutService (scroll-at-top check).
 *  - `distanceForRefresh` – pull distance (px) required to trigger a refresh.
 *                           Defaults to half the current viewport height at pull time.
 *
 * Output:
 *  - `refresh` – emits a Subject<void>. The consumer must call `.next()` on that
 *    subject when the async work is done so the component snaps back into place.
 *    This matches the contract previously provided by ngx-pull-to-refresh.
 */
@Component({
    selector: 'pf-pull-to-refresh',
    templateUrl: './pf-pull-to-refresh.component.html',
    styleUrls: ['./pf-pull-to-refresh.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PfPullToRefreshComponent implements OnInit, OnDestroy {
    private cdr = inject(ChangeDetectorRef);
    private host = inject(ElementRef) as ElementRef<HTMLElement>;

    /** Enable / disable pull-to-refresh (gated externally by LayoutService). */
    @Input() isEnable = false;

    /**
     * Distance in pixels the user must pull to trigger a refresh.
     * Defaults to half the viewport height at the moment of the pull.
     */
    @Input() distanceForRefresh?: number;

    /** Emits a Subject<void>; caller must call .next() when refresh work is done. */
    @Output() refresh = new EventEmitter<Subject<void>>();

    /** Tracks whether we're in the "refreshing" (spinner) state. */
    isRefreshing = false;

    /** Pull progress as a 0-1 ratio of distanceForRefresh. Used for indicator opacity/rotate. */
    pullRatio = 0;

    /** How far (px) the overlay indicator has descended from its hidden spot. */
    indicatorOffset = 0;

    /** When true, the indicator transform/opacity animate (release/park); false during the drag for 1:1 tracking. */
    indicatorAnimating = false;

    private _startY = 0;
    private _currentDelta = 0;
    private _active = false;
    private _done$ = new Subject<void>();
    /** True when the touch started inside a scroll container that wasn't at its top. */
    private _insideScroller = false;

    // Bound handler references kept so we can remove the same function on destroy.
    private _onTouchStart = this.handleTouchStart.bind(this);
    private _onTouchMove = this.handleTouchMove.bind(this);
    private _onTouchEnd = this.handleTouchEnd.bind(this);

    get threshold(): number {
        return this.distanceForRefresh ?? window.innerHeight / 2;
    }

    /** Circumference of the progress ring (bound to stroke-dasharray). */
    readonly ringCircumference = RING_CIRCUMFERENCE;

    /**
     * Stroke-dashoffset for the progress arc: full (empty ring) at rest,
     * 0 (full ring) once the pull reaches the threshold.
     */
    get ringDashoffset(): number {
        return RING_CIRCUMFERENCE * (1 - Math.min(this.pullRatio, 1));
    }

    /** Subtle rotation of the center glyph as the user pulls. */
    get glyphRotation(): number {
        return Math.min(this.pullRatio, 1) * 180;
    }

    /** Opacity of the pull indicator: fades in as the user pulls. */
    get indicatorOpacity(): number {
        return Math.min(this.pullRatio * 5, 1);
    }

    ngOnInit(): void {
        const el = this.host.nativeElement;
        // passive: true lets the browser scroll freely — we never call preventDefault.
        el.addEventListener('touchstart', this._onTouchStart, {
            passive: true,
        });
        el.addEventListener('touchmove', this._onTouchMove, { passive: true });
        el.addEventListener('touchend', this._onTouchEnd, { passive: true });
    }

    private handleTouchStart(event: TouchEvent): void {
        if (!this.isEnable || this.isRefreshing) return;
        this._startY = event.touches[0].clientY;
        this._active = false;
        this._currentDelta = 0;

        // Check if the touch target is inside a scrollable container that isn't at
        // the top — if so we let native scroll win for the entire gesture.
        const scroller = findScrollableAncestor(
            event.target as HTMLElement,
            this.host.nativeElement
        );
        this._insideScroller = scroller != null && scroller.scrollTop > 0;
    }

    private handleTouchMove(event: TouchEvent): void {
        if (!this.isEnable || this.isRefreshing || this._insideScroller) return;

        const delta = event.touches[0].clientY - this._startY;

        // Ignore upward swipes.
        if (delta <= 0) return;

        if (delta < DEAD_ZONE) return;

        this._active = true;
        this._currentDelta = delta;
        this.pullRatio = delta / this.threshold;

        // Overlay pattern: slide only the indicator down (1:1 with the finger,
        // capped) — the content and header stay fixed.
        this.indicatorAnimating = false;
        this.indicatorOffset = Math.min(delta, INDICATOR_TRAVEL);

        this.cdr.markForCheck();
    }

    private handleTouchEnd(): void {
        if (!this.isEnable || !this._active) return;

        if (this._currentDelta >= this.threshold) {
            this.triggerRefresh();
        } else {
            this.snapBack();
        }
    }

    private triggerRefresh(): void {
        this.isRefreshing = true;
        this.pullRatio = 0;
        // Park the indicator on-screen while refreshing.
        this.indicatorAnimating = true;
        this.indicatorOffset = INDICATOR_TRAVEL;
        this.cdr.markForCheck();

        const done$ = new Subject<void>();
        this._done$ = done$;
        done$.subscribe(() => {
            this.isRefreshing = false;
            this.snapBack();
        });

        this.refresh.emit(done$);
    }

    private snapBack(): void {
        this._active = false;
        this._currentDelta = 0;
        this.pullRatio = 0;
        this.indicatorAnimating = true;
        this.indicatorOffset = 0;
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        const el = this.host.nativeElement;
        el.removeEventListener('touchstart', this._onTouchStart);
        el.removeEventListener('touchmove', this._onTouchMove);
        el.removeEventListener('touchend', this._onTouchEnd);
        this._done$.complete();
    }
}
